/**
 * Component: Header location selector
 * The teal-bar trigger, its panel and the confirmation toast.
 *
 * UI only: every piece of state, storage and geocoding belongs to window.HudsonLocation, so two
 * instances (bar and drawer) cannot drift and the PDP reads the same source.
 *
 * The panel is moved to <body> on connect — .wt-announcement is overflow:hidden for the marquee
 * and would clip it. Desktop is a non-modal popover (trapping focus in one is a keyboard trap);
 * the mobile sheet covers the page and is modal.
 */
(() => {
  const LABEL = "[location selector]";
  const DESKTOP_MEDIA = "(min-width: 900px)";
  const LOCATION_TOKEN = "[location]";
  const OPEN_CLASS = "_open";
  const ERROR_CLASS = "_error";
  const BODY_OPEN_CLASS = "location-overlay-on";
  /* The theme's own modal lock (main.css:10456), so the page cannot scroll out from under the
     panel — the desktop popover is positioned once, in document coordinates, and scrolling away
     from it reads as broken. */
  const BODY_LOCK_CLASS = "overflow-hidden";
  const PANEL_GAP_PX = 12;

  const locationState = window.HudsonLocation;

  class LocationSelector extends HTMLElement {
    connectedCallback() {
      if (!locationState) {
        console.error(`${LABEL} hudson-location.js did not load, the selector is disabled.`);
        return;
      }

      this.trigger = this.querySelector("[data-location-trigger]");
      this.labelNode = this.querySelector("[data-location-label]");
      this.layer = this.layer || this.querySelector("[data-location-layer]");
      if (!this.trigger || !this.labelNode || !this.layer) return;

      this.overlay = this.layer.querySelector("[data-location-overlay]");
      this.panel = this.layer.querySelector("[data-location-panel]");
      this.form = this.layer.querySelector("[data-location-form]");
      this.input = this.layer.querySelector("[data-location-input]");
      this.submit = this.layer.querySelector("[data-location-submit]");
      this.status = this.layer.querySelector("[data-location-status]");
      this.geoButton = this.layer.querySelector("[data-location-geo]");
      this.divider = this.layer.querySelector("[data-location-divider]");
      this.toast = this.layer.querySelector("[data-location-toast]");
      this.toastText = this.layer.querySelector("[data-location-toast-text]");

      this.messages = this.readMessages();
      this.desktopQuery = window.matchMedia(DESKTOP_MEDIA);

      if (this.layer.parentElement !== document.body) document.body.appendChild(this.layer);

      this.revealGeolocation();
      this.paintLabel(locationState.get());
      this.bind();
      this.autoDetect();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      /* trapFocus registers its handlers on document (global.js), so this.listeners cannot reach
         them: disconnecting while the sheet is open would leave Tab bouncing between buttons in
         a detached panel. removeTrapFocus is idempotent. */
      if (this.panel?.hasAttribute("aria-modal")) {
        removeTrapFocus();
        this.panel.removeAttribute("aria-modal");
      }
      this.layer?.remove();
      document.body.classList.remove(BODY_OPEN_CLASS, BODY_LOCK_CLASS);
    }

    readMessages() {
      const node = this.querySelector("[data-location-messages]");

      try {
        return JSON.parse(node?.textContent) || {};
      } catch (error) {
        console.error(`${LABEL} messages are not valid JSON, falling back to error codes.`, error);
        return {};
      }
    }

    bind() {
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.trigger.addEventListener("click", () => this.toggle(), { signal });
      this.form?.addEventListener("submit", this.handleSubmit.bind(this), { signal });
      this.input?.addEventListener("input", () => this.clearError(), { signal });
      this.geoButton?.addEventListener("click", this.handleGeolocate.bind(this), { signal });
      this.overlay?.addEventListener("click", () => this.close({ returnFocus: false }), { signal });

      this.layer
        .querySelector("[data-location-close]")
        ?.addEventListener("click", () => this.close(), { signal });
      this.layer
        .querySelector("[data-location-change]")
        ?.addEventListener("click", () => this.open(), { signal });
      this.layer
        .querySelector("[data-location-dismiss]")
        ?.addEventListener("click", () => this.hideToast(), { signal });

      document.addEventListener("keydown", this.handleKeydown.bind(this), { signal });
      document.addEventListener("click", this.handleDocumentClick.bind(this), { signal });
      document.addEventListener(
        locationState.EVENT,
        (event) => this.paintLabel(event.detail.location),
        { signal },
      );
    }

    revealGeolocation() {
      if (!this.geoButton) return;
      if (!window.isSecureContext || !navigator.geolocation) return;

      this.geoButton.hidden = false;
      if (this.divider) this.divider.hidden = false;
    }

    async autoDetect() {
      if (!this.geoButton || this.geoButton.hidden) return;

      const payload = await locationState.autoDetect({
        allowPrompt: this.dataset.autoRequest === "true",
      });
      if (!this.isConnected || !payload) return;
      this.showToast(payload);
    }

    get isDesktop() {
      return this.desktopQuery.matches;
    }

    get isOpen() {
      return this.classList.contains(OPEN_CLASS);
    }

    paintLabel(payload) {
      this.labelNode.textContent = payload?.label || this.messages.set_location || "";
      this.trigger.classList.toggle("wt-location-selector__trigger--set", Boolean(payload?.label));
    }

    position() {
      const triggerRect = this.trigger.getBoundingClientRect();
      this.layer.style.setProperty(
        "--location-layer-top",
        `${Math.round(triggerRect.bottom + window.scrollY + PANEL_GAP_PX)}px`,
      );

      /* Hang the panel off the edge the trigger actually sits at: the merchant can drag the block
         to either end of the bar, and a panel pinned to the far edge reads as broken. */
      this.layer.classList.toggle(
        "wt-location-selector__layer--end",
        triggerRect.left + triggerRect.width / 2 > window.innerWidth / 2,
      );

      const headerSections = document.querySelectorAll(".shopify-section-group-header-group");
      const headerBottom = Array.from(headerSections).reduce(
        (lowest, section) => Math.max(lowest, section.getBoundingClientRect().bottom),
        triggerRect.bottom,
      );
      this.layer.style.setProperty("--location-overlay-top", `${Math.round(headerBottom)}px`);
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    open() {
      this.hideToast();
      this.position();

      this.classList.add(OPEN_CLASS);
      this.layer.classList.add(OPEN_CLASS);
      document.body.classList.add(BODY_OPEN_CLASS, BODY_LOCK_CLASS);
      this.panel.hidden = false;
      this.overlay.hidden = false;
      this.trigger.setAttribute("aria-expanded", "true");

      if (this.isDesktop) {
        this.panel.removeAttribute("aria-modal");
        this.input?.focus();
      } else {
        this.panel.setAttribute("aria-modal", "true");
        trapFocus(this.panel, this.input || this.panel);
      }
    }

    close({ returnFocus = true } = {}) {
      if (!this.isOpen) return;

      this.classList.remove(OPEN_CLASS);
      this.layer.classList.remove(OPEN_CLASS);
      document.body.classList.remove(BODY_OPEN_CLASS, BODY_LOCK_CLASS);
      this.panel.hidden = true;
      this.overlay.hidden = true;
      this.trigger.setAttribute("aria-expanded", "false");

      if (this.panel.hasAttribute("aria-modal")) removeTrapFocus(returnFocus ? this.trigger : null);
      else if (returnFocus) this.trigger.focus();

      this.panel.removeAttribute("aria-modal");
    }

    handleKeydown(event) {
      if (event.key !== "Escape" || !this.isOpen) return;
      this.close();
    }

    handleDocumentClick(event) {
      /* The toast has no scrim of its own — it is a notice, not a dialog — so a click anywhere
         that is not in it dismisses it, the trigger included (opening the panel hides it anyway).
         Its own Change / Dismiss controls are inside it and keep their handlers. */
      if (this.toast && !this.toast.hidden && !this.toast.contains(event.target)) this.hideToast();

      if (!this.isOpen || !this.isDesktop) return;
      if (this.layer.contains(event.target) || this.contains(event.target)) return;
      this.close({ returnFocus: false });
    }

    setStatus(text, isError) {
      if (!this.status) return;

      this.status.textContent = text || "";
      this.status.classList.toggle(ERROR_CLASS, Boolean(isError));
      this.input?.setAttribute("aria-invalid", isError ? "true" : "false");
      this.input?.classList.toggle(ERROR_CLASS, Boolean(isError));
    }

    clearError() {
      if (!this.status?.textContent) return;
      this.setStatus("", false);
    }

    messageFor(error) {
      const copy = this.messages.errors?.[error?.code];
      if (copy) return copy;

      console.warn(`${LABEL} no copy for error code ${error?.code}, showing the network message.`);
      return this.messages.errors?.NETWORK || "";
    }

    setBusy(isBusy) {
      this.panel.setAttribute("aria-busy", isBusy ? "true" : "false");
      if (this.submit) this.submit.disabled = isBusy;
      if (this.geoButton) this.geoButton.disabled = isBusy;
    }

    async run(work) {
      this.setBusy(true);
      this.setStatus(this.messages.looking_up, false);

      try {
        const payload = await work();
        this.setStatus("", false);
        this.close();
        this.showToast(payload);
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.setStatus(this.messageFor(error), true);
        this.input?.focus();
      } finally {
        this.setBusy(false);
      }
    }

    handleSubmit(event) {
      event.preventDefault();
      this.run(() => locationState.setZip(this.input.value));
    }

    handleGeolocate() {
      this.run(() => locationState.useCurrentPosition());
    }
    
    showToast(payload) {
      if (!this.toast || !this.toastText || !payload?.label) return;

      const template = this.messages.confirmed || `${LOCATION_TOKEN}`;
      const [before, after = ""] = template.split(LOCATION_TOKEN);
      const place = document.createElement("strong");
      place.className = "wt-location-selector__toast-place";
      place.textContent = payload.label;

      this.toastText.replaceChildren(document.createTextNode(before), place);
      if (after) this.toastText.appendChild(document.createTextNode(after));

      this.position();
      this.toast.hidden = false;
    }

    hideToast() {
      if (this.toast) this.toast.hidden = true;
    }
  }

  if (!customElements.get("location-selector")) {
    customElements.define("location-selector", LocationSelector);
  }
})();
